import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { DateTimeRangeFilter, TableActionMenu, type TableActionItem } from '@/components/shared';
import {
  fetchBackofficeUserCommunicationSummary,
  fetchBackofficeUserEmailCommunications,
  fetchBackofficeUserInAppCommunications,
} from '../api';
import type {
  BackofficeCommunicationResponse,
  BackofficeUserCommunicationSummary,
  CommunicationStatus,
  EmailChannelStatus,
  InAppChannelStatus,
  SortDirection,
  UserEmailCommunicationSortBy,
  UserInAppCommunicationSortBy,
} from '../types';
import { UserCommunicationDetailModal } from './UserCommunicationDetailModal';
import { UserCommunicationSummaryTiles } from './UserCommunicationSummaryTiles';
import { UserEmailCommunicationsTable } from './UserEmailCommunicationsTable';
import { UserInAppCommunicationsTable } from './UserInAppCommunicationsTable';

const DEFAULT_LIMIT = 20;
const COMMUNICATION_TABS = new Set(['email', 'in-app']);
const EMAIL_SORTS = new Set<UserEmailCommunicationSortBy>([
  'id',
  'subject',
  'templateKey',
  'status',
  'channelStatus',
  'providerMessageId',
  'createdAt',
  'sentAt',
]);
const IN_APP_SORTS = new Set<UserInAppCommunicationSortBy>([
  'id',
  'templateKey',
  'status',
  'channelStatus',
  'createdAt',
  'deliveredAt',
  'readAt',
]);
const SORT_DIRECTIONS = new Set<SortDirection>(['asc', 'desc']);
const COMMUNICATION_STATUSES: CommunicationStatus[] = [
  'queued',
  'processing',
  'sent',
  'failed',
  'dead_letter',
  'cancelled',
];
const EMAIL_CHANNEL_STATUSES: EmailChannelStatus[] = [
  'queued',
  'sending',
  'sent',
  'soft_failed',
  'hard_failed',
  'bounced',
];
const IN_APP_CHANNEL_STATUSES: InAppChannelStatus[] = ['queued', 'delivered', 'read', 'failed'];

type CommunicationTab = 'email' | 'in-app';

export interface UserCommunicationsSectionProps {
  userId: string;
  userDisplayName: string;
  surface?: 'page' | 'tile' | undefined;
  channel?: CommunicationTab | undefined;
  showSummary?: boolean | undefined;
  showSubtabs?: boolean | undefined;
}

function readCommunicationTab(value: string | null): CommunicationTab {
  return COMMUNICATION_TABS.has(value ?? '') ? (value as CommunicationTab) : 'email';
}

function readSortDirection(value: string | null): SortDirection {
  return SORT_DIRECTIONS.has(value as SortDirection) ? (value as SortDirection) : 'desc';
}

function readEmailSort(value: string | null): UserEmailCommunicationSortBy {
  return EMAIL_SORTS.has(value as UserEmailCommunicationSortBy)
    ? (value as UserEmailCommunicationSortBy)
    : 'createdAt';
}

function readInAppSort(value: string | null): UserInAppCommunicationSortBy {
  return IN_APP_SORTS.has(value as UserInAppCommunicationSortBy)
    ? (value as UserInAppCommunicationSortBy)
    : 'createdAt';
}

function labelForStatus(value: string): string {
  return value
    .split('_')
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

export function UserCommunicationsSection({
  userId,
  userDisplayName,
  surface = 'page',
  channel,
  showSummary = true,
  showSubtabs = true,
}: UserCommunicationsSectionProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState<BackofficeUserCommunicationSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [communications, setCommunications] = useState<BackofficeCommunicationResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<BackofficeCommunicationResponse | null>(null);
  const refreshAbortControllerRef = useRef<AbortController | null>(null);

  const activeTab = channel ?? readCommunicationTab(searchParams.get('communicationTab'));
  const emailSortBy = readEmailSort(searchParams.get('emailSortBy'));
  const emailSortDirection = readSortDirection(searchParams.get('emailSortDirection'));
  const inAppSortBy = readInAppSort(searchParams.get('inAppSortBy'));
  const inAppSortDirection = readSortDirection(searchParams.get('inAppSortDirection'));
  const messageId = searchParams.get('messageId') ?? '';
  const emailStatus = (searchParams.get('emailStatus') ?? '') as CommunicationStatus | '';
  const emailChannelStatus = (searchParams.get('emailChannelStatus') ?? '') as
    EmailChannelStatus | '';
  const emailSentFrom = searchParams.get('emailSentFrom') ?? '';
  const emailSentTo = searchParams.get('emailSentTo') ?? '';
  const inAppStatus = (searchParams.get('inAppStatus') ?? '') as CommunicationStatus | '';
  const inAppChannelStatus = (searchParams.get('inAppChannelStatus') ?? '') as
    InAppChannelStatus | '';
  const hasMore = communications.length < total;

  const requestParams = useMemo(() => {
    const params = new URLSearchParams();
    if (activeTab === 'email') {
      params.set('sortBy', emailSortBy);
      params.set('sortDirection', emailSortDirection);
      if (messageId) params.set('messageId', messageId);
      if (emailStatus) params.set('status', emailStatus);
      if (emailChannelStatus) params.set('channelStatus', emailChannelStatus);
      if (emailSentFrom) params.set('sentFrom', emailSentFrom);
      if (emailSentTo) params.set('sentTo', emailSentTo);
    } else {
      params.set('sortBy', inAppSortBy);
      params.set('sortDirection', inAppSortDirection);
      if (inAppStatus) params.set('status', inAppStatus);
      if (inAppChannelStatus) params.set('channelStatus', inAppChannelStatus);
    }
    return params;
  }, [
    activeTab,
    emailChannelStatus,
    emailSentFrom,
    emailSentTo,
    emailSortBy,
    emailSortDirection,
    emailStatus,
    inAppChannelStatus,
    inAppSortBy,
    inAppSortDirection,
    inAppStatus,
    messageId,
  ]);

  const loadCommunications = useCallback(
    async (offset: number, signal: AbortSignal) => {
      const params = new URLSearchParams(requestParams);
      params.set('limit', String(DEFAULT_LIMIT));
      params.set('offset', String(offset));
      const response =
        activeTab === 'email'
          ? await fetchBackofficeUserEmailCommunications(userId, params, signal)
          : await fetchBackofficeUserInAppCommunications(userId, params, signal);

      setTotal(response.page.total);
      setCommunications((current) =>
        offset === 0 ? response.communications : [...current, ...response.communications]
      );
    },
    [activeTab, requestParams, userId]
  );

  const handleRefresh = useCallback(async () => {
    refreshAbortControllerRef.current?.abort();
    const controller = new AbortController();
    refreshAbortControllerRef.current = controller;

    setRefreshing(true);
    setError(null);
    try {
      await loadCommunications(0, controller.signal);
    } catch (caught: unknown) {
      if (caught instanceof DOMException && caught.name === 'AbortError') {
        return;
      }
      setCommunications([]);
      setTotal(0);
      setError(caught instanceof Error ? caught.message : 'Failed to refresh communications');
    } finally {
      if (refreshAbortControllerRef.current === controller) {
        refreshAbortControllerRef.current = null;
      }
      if (!controller.signal.aborted) {
        setRefreshing(false);
      }
    }
  }, [loadCommunications]);

  useEffect(() => {
    if (!showSummary) {
      setSummary(null);
      setSummaryError(null);
      setSummaryLoading(false);
      return;
    }

    const controller = new AbortController();
    setSummaryLoading(true);
    setSummaryError(null);

    fetchBackofficeUserCommunicationSummary(userId, controller.signal)
      .then((response) => {
        if (!controller.signal.aborted) {
          setSummary(response);
        }
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') {
          return;
        }
        setSummary(null);
        setSummaryError(caught instanceof Error ? caught.message : 'Failed to load preferences');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setSummaryLoading(false);
        }
      });

    return () => controller.abort();
  }, [showSummary, userId]);

  useEffect(() => {
    const controller = new AbortController();
    setInitialLoading(true);
    setError(null);
    setSelected(null);

    loadCommunications(0, controller.signal)
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') {
          return;
        }
        setCommunications([]);
        setTotal(0);
        setError(caught instanceof Error ? caught.message : 'Failed to load communications');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setInitialLoading(false);
        }
      });

    return () => controller.abort();
  }, [loadCommunications]);

  useEffect(() => () => refreshAbortControllerRef.current?.abort(), []);

  function updateParams(updates: Record<string, string | null>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(updates)) {
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
    }
    setSearchParams(next, { replace: true });
  }

  function getSubtabHref(tab: CommunicationTab) {
    const next = new URLSearchParams(searchParams);
    if (tab === 'email') {
      next.delete('communicationTab');
    } else {
      next.set('communicationTab', tab);
    }
    return `/users/${userId}?${next.toString()}`;
  }

  function getSubtabClassName(tab: CommunicationTab) {
    return [
      'px-1 pb-3 text-body font-semibold',
      activeTab === tab
        ? 'border-b-2 border-neutral-900 text-neutral-900'
        : 'border-b-2 border-transparent text-neutral-500 hover:text-backoffice-600',
    ].join(' ');
  }

  async function loadMore() {
    setLoadingMore(true);
    setError(null);
    const controller = new AbortController();
    try {
      await loadCommunications(communications.length, controller.signal);
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : 'Failed to load more communications');
    } finally {
      setLoadingMore(false);
    }
  }

  const tableActions = useMemo<TableActionItem[]>(
    () => [
      {
        id: 'refresh',
        label: 'Refresh',
        loadingLabel: 'Refreshing',
        icon: RefreshCw,
        onSelect: () => {
          void handleRefresh();
        },
        placement: 'fixed-inline',
        disabled: initialLoading,
        isLoading: refreshing,
      },
    ],
    [handleRefresh, initialLoading, refreshing]
  );

  function renderFilters() {
    return (
      <div className="flex flex-wrap items-end gap-3 border-b border-neutral-200 bg-neutral-50 px-5 py-4">
        {activeTab === 'email' ? (
          <>
            <label className="flex min-w-[220px] flex-col gap-1 text-small font-medium text-neutral-700">
              Message ID
              <input
                type="text"
                value={messageId}
                onChange={(event) => updateParams({ messageId: event.target.value.trim() })}
                placeholder="Message or provider ID"
                className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-body text-neutral-900"
              />
            </label>
            <StatusSelect
              label="Status"
              value={emailStatus}
              options={COMMUNICATION_STATUSES}
              onChange={(value) => updateParams({ emailStatus: value })}
            />
            <StatusSelect
              label="Channel status"
              value={emailChannelStatus}
              options={EMAIL_CHANNEL_STATUSES}
              onChange={(value) => updateParams({ emailChannelStatus: value })}
            />
            <DateTimeRangeFilter
              label="Sent range"
              from={emailSentFrom}
              to={emailSentTo}
              onRangeChange={(from, to) => updateParams({ emailSentFrom: from, emailSentTo: to })}
            />
          </>
        ) : (
          <>
            <StatusSelect
              label="Status"
              value={inAppStatus}
              options={COMMUNICATION_STATUSES}
              onChange={(value) => updateParams({ inAppStatus: value })}
            />
            <StatusSelect
              label="Channel status"
              value={inAppChannelStatus}
              options={IN_APP_CHANNEL_STATUSES}
              onChange={(value) => updateParams({ inAppChannelStatus: value })}
            />
          </>
        )}
        <div className="flex w-full justify-end md:ml-auto md:min-w-[12rem] md:flex-1">
          <TableActionMenu
            actions={tableActions}
            label={`${activeTab === 'email' ? 'Email' : 'In-app'} communication table actions`}
            containerClassName="w-auto md:w-full"
          />
        </div>
      </div>
    );
  }

  return (
    <section
      className={
        surface === 'tile'
          ? 'flex h-full min-h-full flex-col gap-5 bg-white'
          : 'flex flex-col gap-5'
      }
    >
      {showSummary ? (
        <UserCommunicationSummaryTiles
          summary={summary}
          loading={summaryLoading}
          error={summaryError}
        />
      ) : null}

      {showSubtabs ? (
        <div className="flex gap-5 border-b border-neutral-200">
          <Link
            to={getSubtabHref('email')}
            className={getSubtabClassName('email')}
            aria-current={activeTab === 'email' ? 'page' : undefined}
          >
            Email
          </Link>
          <Link
            to={getSubtabHref('in-app')}
            className={getSubtabClassName('in-app')}
            aria-current={activeTab === 'in-app' ? 'page' : undefined}
          >
            In-app notifications
          </Link>
        </div>
      ) : null}

      <section
        className={
          surface === 'tile'
            ? 'flex min-h-0 flex-1 flex-col overflow-hidden bg-white'
            : 'rounded-lg border border-neutral-200 bg-white shadow-sm'
        }
      >
        {renderFilters()}

        {initialLoading ? (
          <div className="px-5 py-16 text-center text-body text-neutral-500">
            Loading communications
          </div>
        ) : error ? (
          <div className="px-5 py-16 text-center text-body text-red-700">{error}</div>
        ) : communications.length === 0 ? (
          <div className="px-5 py-16 text-center text-body text-neutral-500">
            No communications found for {userDisplayName}.
          </div>
        ) : activeTab === 'email' ? (
          <div className={surface === 'tile' ? 'min-h-0 flex-1 overflow-y-auto' : undefined}>
            <UserEmailCommunicationsTable
              communications={communications}
              sortBy={emailSortBy}
              sortDirection={emailSortDirection}
              onSortChange={(nextSortBy) => {
                const nextDirection =
                  emailSortBy === nextSortBy && emailSortDirection === 'asc' ? 'desc' : 'asc';
                updateParams({ emailSortBy: nextSortBy, emailSortDirection: nextDirection });
              }}
              onSelect={setSelected}
            />
          </div>
        ) : (
          <div className={surface === 'tile' ? 'min-h-0 flex-1 overflow-y-auto' : undefined}>
            <UserInAppCommunicationsTable
              communications={communications}
              sortBy={inAppSortBy}
              sortDirection={inAppSortDirection}
              onSortChange={(nextSortBy) => {
                const nextDirection =
                  inAppSortBy === nextSortBy && inAppSortDirection === 'asc' ? 'desc' : 'asc';
                updateParams({ inAppSortBy: nextSortBy, inAppSortDirection: nextDirection });
              }}
              onSelect={setSelected}
            />
          </div>
        )}

        {!initialLoading && !error && communications.length > 0 ? (
          <div className="flex items-center justify-between gap-3 px-5 py-4 text-small text-neutral-500">
            <span>
              Showing {communications.length} of {total} communications
            </span>
            {hasMore ? (
              <button
                type="button"
                className="focus-ring rounded-lg border border-neutral-200 bg-white px-3 py-2 font-medium text-neutral-700 hover:bg-backoffice-50 disabled:cursor-not-allowed disabled:text-neutral-400"
                disabled={loadingMore}
                onClick={() => {
                  void loadMore();
                }}
              >
                {loadingMore ? 'Loading more' : 'Load more'}
              </button>
            ) : null}
          </div>
        ) : null}
      </section>

      {selected ? (
        <UserCommunicationDetailModal
          communication={selected}
          channel={activeTab}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </section>
  );
}

function StatusSelect<TValue extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: TValue | '';
  options: TValue[];
  onChange: (value: TValue | '') => void;
}) {
  return (
    <label className="flex min-w-[170px] flex-col gap-1 text-small font-medium text-neutral-700">
      {label}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value as TValue | '')}
        className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-body text-neutral-900"
      >
        <option value="">All</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {labelForStatus(option)}
          </option>
        ))}
      </select>
    </label>
  );
}
